/** LaserHandler, ProjectileEmitter (+ CannonballProjectile, BluePlasmaProjectile, SpikeyBall), LauncherBase. */
import { GameBehaviour, SOUND_TYPES } from '../game.ts';
import { BodyType, type SimBody, type SimCollider, type SimGo } from '../world.ts';
import { FrameCounter, PrimaryWaysToDie, clamp, pingPong, vnorm, type Deathod, type RGBA, type V2 } from '../unity.ts';
import { compose, invert, mul, type Xform } from '../../../model/xform.ts';
import type { SpriteRef } from '../../../data/gameData.ts';
import { bool, list, num, rgba } from '../props.ts';
import { isRespawnable, PlayerController, PowerupType, type Respawnable } from '../player.ts';
import { ActivatorBase, resolveActivators } from './activators.ts';
import { DespawnTimer, DynamicObject } from './dynamic.ts';
import { DeathTriggerHandler } from './hazards.ts';
import { Animator } from './animator.ts';


/** set a transform's world rotation (and optionally its local x scale sign), keeping its position */
function setWorldRotation(t: SimGo, deg: number, scaleX?: number) {
  const [sx, sy] = t.lossyScale;
  const p = t.position;
  const w = compose(p.x, p.y, deg, scaleX === undefined ? sx : Math.abs(sx) * scaleX, sy);
  t.setLocal(t.parent ? mul(invert(t.parent.worldXform()), w) : w);
}

// ---------- LaserHandler ----------

export class LaserHandler extends GameBehaviour {
  static deathod: Deathod = { primary: PrimaryWaysToDie.Laser };
  activators: ActivatorBase[] = [];
  activationType = 0;
  color1: RGBA = [1, 0, 1, 1];
  color2: RGBA = [0, 0, 1, 1];
  lerpDuration = 0.4;
  lerpTime = 0;
  wasOn: boolean | null = null;
  override noSave = ['activators'];

  override awake() {
    const d = this.data;
    this.activationType = num(d.activationType);
    this.color1 = rgba(d.color1);
    this.color2 = rgba(d.color2);
    this.lerpDuration = num(d.lerpDuration, 0.4);
  }

  override start() {
    this.activators = resolveActivators(this, list(this.data.activators));
  }

  override update(dt: number) {
    this.lerpTime += dt;
    const t = pingPong(this.lerpTime / this.lerpDuration, 1);
    const a = this.color1, b = this.color2;
    if (this.go.sprite) this.go.sprite.color = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t];
  }

  override fixedUpdate() {
    const on = this.activationStatus();
    if (on && this.wasOn === false) this.sound('laser');
    this.wasOn = on;
    if (this.go.sprite) this.go.sprite.enabled = on;
    const col = this.go.colliders.find((c) => c.type === 'BoxCollider2D');
    if (col) col.enabled = on;
  }

  private activationStatus(): boolean {
    if (!this.activators.length) return true;
    if (this.activationType === 0) return this.activators.every((a) => a.isActivated);
    if (this.activationType === 1) return this.activators.some((a) => a.isActivated);
    return false;
  }

  private laserLogic(other: SimCollider) {
    if (!this.activationStatus()) return;
    const r = other.go.behaviours.find(isRespawnable) as Respawnable | undefined;
    r?.beginDeath(LaserHandler.deathod);
  }

  override onTriggerEnter(other: SimCollider) {
    this.laserLogic(other);
  }
  override onTriggerStay(other: SimCollider) {
    this.laserLogic(other);
  }
}

// ---------- ProjectileEmitter ----------

export class ProjectileEmitter extends GameBehaviour {
  activator: ActivatorBase | null = null;
  emitterPoint: SimGo | null = null;
  singleFirePerActivate = false;
  projectileInheritsEmitterVelocity = false;
  fireCounter = new FrameCounter();
  projectileSpeed = 8;
  projectileRotationSpeed = 1;
  recoilObject: SimGo | null = null;
  recoilCounter = new FrameCounter();
  recoilDistance = 0;
  recoilObjectStartPos: Xform | null = null;
  hasFiredSinceActivation = false;
  /** GetComponentInChildren<Animator>(): needs a "fire" trigger */
  firingAnimator: Animator | null = null;
  override noSave = ['activator', 'emitterPoint', 'recoilObject', 'firingAnimator'];

  override awake() {
    const d = this.data;
    this.emitterPoint = this.refGo(d.emitterPoint) ?? this.go;
    this.singleFirePerActivate = bool(d.singleFirePerActivate);
    this.projectileInheritsEmitterVelocity = bool(d.projectileInheritsEmitterVelocity);
    const delay = FrameCounter.from(d.delayCounterBy, 0);
    this.fireCounter = FrameCounter.from(d.fireCounter, 0);
    this.fireCounter.counter = this.fireCounter.frames - delay.frames;
    this.fireCounter.start();
    this.projectileSpeed = num(d.projectileSpeed, 8);
    this.projectileRotationSpeed = num(d.projectileRotationSpeed, 1);
    this.recoilObject = this.refGo(d.recoilObject);
    this.recoilCounter = FrameCounter.from(d.recoilCounter, 0);
    this.recoilDistance = num(d.recoilDistance);
    if (this.recoilObject) this.recoilObjectStartPos = this.recoilObject.local;
  }

  override start() {
    this.firingAnimator = this.go.getBehaviourInChildren(Animator, false);
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
  }

  private isActive() {
    return !this.activator || this.activator.isActivated;
  }

  override fixedUpdate() {
    this.fireCounter.count();
    const active = this.isActive();
    if (this.fireCounter.isFinished() && active && (!this.hasFiredSinceActivation || !this.singleFirePerActivate)) {
      this.hasFiredSinceActivation = true;
      this.fire();
      this.fireCounter.restart();
    }
    this.recoilCounter.count();
    if (this.recoilCounter.isRunningNotFinished() && this.recoilObject && this.recoilObjectStartPos) {
      const s = this.recoilObjectStartPos;
      const off = this.recoilDistance * (1 - Math.abs(2 * this.recoilCounter.percentage - 1));
      this.recoilObject.setLocal({ ...s, tx: s.tx - off });
    }
    if (!active) this.hasFiredSinceActivation = false;
  }

  private fire() {
    const guid: string | undefined = this.data.projectilePrefab?.guid;
    if (!guid) return;
    this.recoilCounter.restart();
    this.firingAnimator?.setTrigger('fire');
    const ep = this.emitterPoint ?? this.go;
    const m = ep.worldXform();
    // TransformDirection(right); a mirrored emitter (the editor stores Y-rotations as negative x scale) fires mirrored
    const dir = vnorm({ x: m.a, y: m.b });
    const rot = (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
    const proj = this.game.instantiate(guid, ep.position, rot);
    const sound = SOUND_TYPES[num(this.data.fireSound)];
    if (sound && sound !== 'None') this.sound(sound, ep.position);
    else if (proj?.getBehaviour(BluePlasmaProjectile)) this.sound('plasmaShot', ep.position);
    else if (proj) this.sound('cannon', ep.position);
    if (!proj) return;
    const rb = proj.body;
    if (rb) {
      rb.velocity = { x: dir.x * this.projectileSpeed, y: dir.y * this.projectileSpeed };
      rb.angularVelocity = this.projectileRotationSpeed;
    }
    const d = this.data;
    if (bool(d.overrideProjectileDespawnVars)) {
      const dt = proj.getBehaviour(DespawnTimer);
      if (dt) {
        dt.despawnTimer = FrameCounter.from(d.despawnTimer, 0);
        dt.destroyOnDespawn = bool(d.destroyOnDespawn);
        dt.disableColliderOnFade = bool(d.disableColliderOnFade, true);
        dt.staticRigidBodyOnFade = bool(d.staticRigidBodyOnFade, true);
      }
    }
    const er = this.go.body;
    if (this.projectileInheritsEmitterVelocity && er && rb) {
      const add = er.type === BodyType.Dynamic ? er.velocity : er.hasKV ? er.kvVelocity : { x: 0, y: 0 };
      rb.velocity = { x: rb.vx + add.x, y: rb.vy + add.y };
    }
  }
}

// ---------- projectiles ----------

function closestPoint(c: SimCollider, p: V2): V2 {
  const b = c.bounds();
  if (!b) return p;
  return { x: clamp(p.x, b[0], b[2]), y: clamp(p.y, b[1], b[3]) };
}

export class CannonballProjectile extends GameBehaviour {
  static deathod: Deathod = { primary: PrimaryWaysToDie.Projectile, secondary: 'Cannonball' };
  dynamicForce = 1;
  noRotation = true;
  forceRotateExplosionAnimation = true;
  originalRotation = 0;

  override awake() {
    const d = this.data;
    this.dynamicForce = num(d.dynamicForce, 1);
    this.noRotation = bool(d.noRotation, true);
    this.forceRotateExplosionAnimation = bool(d.forceRotateExplosionAnimation, true);
    this.originalRotation = this.go.rotation;
    if (this.noRotation && this.go.body) this.go.body.rotation = 0;
  }

  private get dt() {
    return this.go.getBehaviour(DespawnTimer);
  }

  override onTriggerEnter(other: SimCollider) {
    if (!this.dt?.fadeOutTimer.isNotRunning()) return;
    other.go.getBehaviour(PlayerController)?.beginDeath(CannonballProjectile.deathod);
    if (!other.isTrigger) this.handleCollision(other);
  }

  handleCollision(other: SimCollider | null = null) {
    const dt = this.dt;
    if (!dt?.fadeOutTimer.isNotRunning()) return;
    dt.startFadeOut();
    if (!other) return;
    this.sound('explosion');
    const rb = this.go.body;
    const orb = other.attachedRigidbody;
    if (!orb) return;
    const v = rb ? vnorm(rb.velocity) : { x: 0, y: 0 };
    orb.addForceAtPosition({ x: v.x * this.dynamicForce, y: v.y * this.dynamicForce }, closestPoint(other, this.go.position));
    if (rb) rb.velocity = { x: 0, y: 0 };
    this.go.getBehaviour(Animator)?.setTrigger('explode');
    if (this.forceRotateExplosionAnimation && rb) rb.rotation = this.originalRotation;
  }
}

export class BluePlasmaProjectile extends GameBehaviour {
  static deathod: Deathod = { primary: PrimaryWaysToDie.Projectile, secondary: 'BluePlasma' };
  fadeOutTimer = new FrameCounter();
  maxTime = new FrameCounter();

  override awake() {
    this.fadeOutTimer = FrameCounter.from(this.data.fadeOutTimer, 0);
    this.maxTime = FrameCounter.from(this.data.maxTime, 0);
    this.maxTime.start();
  }

  override onTriggerEnter(other: SimCollider) {
    if (!this.fadeOutTimer.isNotRunning()) return;
    const p = other.go.getBehaviour(PlayerController);
    if (p) {
      if (p.isDashing()) p.dashCountSinceLastGround--;
      else p.beginDeath(BluePlasmaProjectile.deathod);
    }
    if (!other.isTrigger) this.handleCollision(true);
  }

  handleCollision(hit = false) {
    if (!this.fadeOutTimer.isNotRunning()) return;
    if (hit) this.sound('projectileHit');
    this.fadeOutTimer.start();
    if (this.go.colliders[0]) this.go.colliders[0].enabled = false;
  }

  override fixedUpdate() {
    if (this.maxTime.countCheckFinishReset()) this.handleCollision();
    if (this.fadeOutTimer.countCheckFinishReset()) {
      this.go.destroy();
      return;
    }
    if (this.fadeOutTimer.isRunningNotFinished() && this.go.sprite) {
      const c = this.go.sprite.color;
      this.go.sprite.color = [c[0], c[1], c[2], 1 - this.fadeOutTimer.percentage];
    }
  }
}

export class SpikeyBall extends GameBehaviour {
  face: SimGo | null = null;
  lastPos: V2 = { x: 0, y: 0 };
  faceRotation = 0;
  override noSave = ['face'];

  override awake() {
    // DynamicObject (on the same prefab) adds Squishable too; addToSelf keeps a single one
    this.face = this.refGo(this.data.Face);
  }

  override update() {
    if (this.face) setWorldRotation(this.face, this.faceRotation);
  }

  beginDeath(d: Deathod) {
    if (d.primary !== PrimaryWaysToDie.Squish) return;
    const t = this.go.getBehaviour(DespawnTimer);
    if (t && !t.despawnTimer.isFinished() && t.fadeOutTimer.isNotRunning()) this.sound('spikeyBall');
    t?.despawnTimer.markFinished();
  }

  override fixedUpdate() {
    const p = this.go.position;
    const face = this.face;
    if (face) {
      if (p.x === this.lastPos.x && p.y === this.lastPos.y) setWorldRotation(face, 0);
      else {
        const dx = p.x - this.lastPos.x, dy = p.y - this.lastPos.y;
        this.faceRotation = (Math.atan2(dy, dx) * 180) / Math.PI + (dx < 0 ? 180 : 0);
        setWorldRotation(face, this.faceRotation, dx >= 0 ? -1 : 1);
      }
    }
    this.lastPos = p;
  }
}

// ---------- SpikeyPatroller ----------

/** BoxCollider2D side in world space (Util.GetWorldSide: centre of the side, edge radius added along y) */
function boxSideY(c: SimCollider, top: boolean): number {
  const s = c.shape;
  if (s.kind !== 'box') return c.go.position.y;
  const m = c.go.worldXform();
  const ly = s.oy + (top ? s.h / 2 : -s.h / 2);
  return m.b * s.ox + m.d * ly + m.ty + (top ? s.edge : -s.edge);
}

export class SpikeyPatroller extends GameBehaviour {
  static deathod: Deathod = { primary: PrimaryWaysToDie.Spikes, secondary: 'Patroller' };
  speed = 2;
  jumpVelocity = 12;
  sightDistance = 20;
  sightAngle = 120;
  facingRight = true;
  jumpsOnPlayerJump = true;
  groundOnPlayerPadding = 0.07;
  enableCliffSensing = true;
  useLeftBoundary = false;
  leftBoundaryPosition = 0;
  useRightBoundary = false;
  rightBoundaryPosition = 0;
  groundCheckDist = 0.1;
  centerGroundCheckDist = 0.15;
  wallCheckDist = 0.05;
  maxGroundAngle = 47;
  groundAhead = false;
  wallAhead = false;
  groundedOnBodySpeed: V2 = { x: 0, y: 0 };
  handleJumpNextFrame = false;
  inBounds = true;
  isGrounded = false;
  groundedOnPlayer: PlayerController | null = null;
  groundedOnPatroller: SpikeyPatroller | null = null;
  groundedOnPlayerPosition: V2 = { x: 0, y: 0 };
  wasDashLaunchedDiagonally = false;
  changeDirCounter = new FrameCounter(20);
  regroundOnPlayerCounter = FrameCounter.seconds(0.3);
  lastPos: V2 = { x: 0, y: 0 };
  lastIsSpawned = true;
  groundCheck: SimGo | null = null;
  centerGroundCheck: SimGo | null = null;
  wallCheck: SimGo | null = null;
  collider: SimCollider | null = null;
  dyn: DynamicObject | null = null;
  spriteMaps: { left: SpriteRef | null; right: SpriteRef | null }[] = [];
  override noSave = ['groundCheck', 'centerGroundCheck', 'wallCheck', 'collider', 'dyn', 'spriteMaps'];

  override awake() {
    const d = this.data;
    this.speed = num(d.speed, 2);
    this.jumpVelocity = num(d.jumpVelocity, 12);
    this.sightDistance = num(d.sightDistance, 20);
    this.sightAngle = num(d.sightAngle, 120);
    this.facingRight = bool(d.facingRight, true);
    this.jumpsOnPlayerJump = bool(d.jumpsOnPlayerJump, true);
    this.enableCliffSensing = bool(d.enableCliffSensing, true);
    this.useLeftBoundary = bool(d.useLeftBoundary);
    this.leftBoundaryPosition = num(d.leftBoundaryPosition);
    this.useRightBoundary = bool(d.useRightBoundary);
    this.rightBoundaryPosition = num(d.rightBoundaryPosition);
    this.groundCheckDist = num(d.groundCheckDist, 0.1);
    this.centerGroundCheckDist = num(d.centerGroundCheckDist, 0.15);
    this.wallCheckDist = num(d.wallCheckDist, 0.05);
    this.maxGroundAngle = num(d.maxGroundAngle, 47);
    this.groundCheck = this.refGo(d.groundCheck);
    this.centerGroundCheck = this.refGo(d.centerGroundCheck);
    this.wallCheck = this.refGo(d.wallCheck);
    this.collider = this.go.colliders.find((c) => c.type === 'BoxCollider2D') ?? null;
    this.regroundOnPlayerCounter.markFinished();
    const sh = this.go.comps.get('SpikeyPatrollerSpriteHandler');
    const gd = this.game.gd;
    const sp = (r: any) => (r?.guid ? gd.spriteById.get(`${r.guid}:${r.fileID}`) ?? null : null);
    this.spriteMaps = list(sh?.spriteMaps).map((m: any) => ({ left: sp(m.leftSprite), right: sp(m.rightSprite) }));
    this.game.jumpListeners.add(this);
  }

  override start() {
    this.dyn = this.go.getBehaviour(DynamicObject);
    this.lastIsSpawned = this.dyn?.isSpawned ?? true;
  }

  override onDestroy() {
    this.game.jumpListeners.delete(this);
  }

  onPlayerJumped(p: PlayerController, isWallJump: boolean) {
    if (!this.jumpsOnPlayerJump || isWallJump || this.go.destroyed) return;
    const a = this.go.position, b = p.go.position;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const to = vnorm({ x: b.x - a.x, y: b.y - a.y });
    const fwd = this.facingRight ? 1 : -1;
    const ang = to.x === 0 && to.y === 0 ? 0 : (Math.acos(clamp(to.x * fwd, -1, 1)) * 180) / Math.PI;
    if (dist <= this.sightDistance && ang <= this.sightAngle) this.handleJumpNextFrame = true;
  }

  private isOwn(c: SimCollider) {
    return c.attachedRigidbody === this.go.body;
  }

  private cast(from: SimGo, dir: V2, dist: number) {
    return this.world.raycastAll(from.position, dir, dist, (c) => !c.isTrigger && !this.isOwn(c));
  }

  override fixedUpdate() {
    const rb = this.go.body;
    const dyn = this.dyn;
    if (!rb || !dyn || !this.groundCheck || !this.centerGroundCheck || !this.wallCheck || !this.collider) return;
    const wasGroundedOnPlayer = !!this.groundedOnPlayer || !!this.groundedOnPatroller;
    const spawnChanged = dyn.isSpawned !== this.lastIsSpawned;
    this.lastIsSpawned = dyn.isSpawned;
    const lastLinearVelocity = rb.velocity;
    this.regroundOnPlayerCounter.count();
    this.changeDirCounter.count();
    if (spawnChanged) {
      for (const g of this.go.descendants()) {
        if (!g.active || !g.behaviours.some((b) => b instanceof DeathTriggerHandler)) continue;
        const t = g.colliders.find((c) => c.type === 'BoxCollider2D');
        if (t?.isTrigger) t.enabled = dyn.isSpawned;
      }
    }
    if (!dyn.isSpawned) return;

    this.enumerateContacts();
    if (this.handleJumpNextFrame) {
      this.handleJumpNextFrame = false;
      if (this.isGrounded) {
        rb.vy = this.jumpVelocity;
        this.sound('patroller');
        this.isGrounded = false;
      }
    }

    let hits = this.cast(this.groundCheck, { x: 0, y: -1 }, this.groundCheckDist);
    let count = hits.length;
    for (const h of hits) if (h.collider.go.getBehaviour(PlayerController) && this.regroundOnPlayerCounter.isRunningNotFinished()) count--;
    this.groundAhead = count > 0;

    hits = this.cast(this.wallCheck, { x: this.facingRight ? 1 : -1, y: 0 }, this.wallCheckDist);
    count = hits.length;
    for (const h of hits) {
      const pc = h.collider.go.getBehaviour(PlayerController);
      if (pc && pc.willDie(SpikeyPatroller.deathod)) count--;
    }
    this.wallAhead = count > 0;

    const wasInBounds = this.inBounds;
    this.inBounds = this.isInBounds();
    const pos = this.go.position;
    if (!this.groundedOnPlayer && !this.groundedOnPatroller) {
      const flipCuzSensor = (!this.groundAhead && this.isGrounded && this.enableCliffSensing) || this.wallAhead;
      const flipCuzBounds = !this.inBounds && wasInBounds;
      const dx = pos.x - this.lastPos.x, dy = pos.y - this.lastPos.y;
      const flipCuzStuck = dx * dx + dy * dy < 0.001 * 0.001;
      if (flipCuzBounds || flipCuzSensor || flipCuzStuck) this.flip();
    }
    if (!this.wasDashLaunchedDiagonally) rb.vx = (this.facingRight ? 1 : -1) * this.speed + this.groundedOnBodySpeed.x;

    const gp = this.groundedOnPlayer;
    if (gp) {
      if (gp.characterDirection > 0 !== this.facingRight) this.flip(true);
      const yPos = boxSideY(gp.playerCollider, true) + pos.y - boxSideY(this.collider, false) + this.groundOnPlayerPadding;
      rb.movePosition({ x: gp.body.position.x, y: yPos });
      rb.velocity = gp.body.velocity;
    }
    const gpt = this.groundedOnPatroller;
    if (gpt && gpt.go.body) {
      if (gpt.facingRight !== this.facingRight) this.flip(true);
      const s = this.collider.shape;
      const sizeX = s.kind === 'box' ? s.w : 0, edge = s.kind === 'box' ? s.edge : 0;
      rb.movePosition({ x: gpt.go.body.position.x, y: gpt.go.body.position.y + sizeX + edge * 2 + 0.02 });
      rb.velocity = gpt.go.body.velocity;
    }
    const checkDist = this.centerGroundCheckDist - Math.min((gp?.body.vy ?? 0) + (gpt?.go.body?.vy ?? 0), 0);
    hits = this.cast(this.centerGroundCheck, { x: 0, y: -1 }, checkDist);
    this.groundedOnPlayer = null;
    this.groundedOnPatroller = null;
    for (const h of hits) {
      const pc = h.collider.go.getBehaviour(PlayerController);
      if (pc && this.shouldGroundOnPlayer(pc)) {
        this.groundedOnPlayer = pc;
        this.groundedOnPlayerPosition = h.point;
        break;
      } else if (pc && pc.dashDirection.x !== 0) this.wasDashLaunchedDiagonally ||= pc.dashFinishedThisFrame;
      this.groundedOnPatroller = h.collider.go.getBehaviour(SpikeyPatroller);
      if (this.groundedOnPatroller) {
        this.groundedOnPlayerPosition = h.point;
        break;
      }
    }
    if (wasGroundedOnPlayer && !this.groundedOnPlayer) {
      rb.velocity = lastLinearVelocity;
      this.regroundOnPlayerCounter.restart();
    }
    this.lastPos = this.go.position;
  }

  private isInBounds() {
    const x = this.wallCheck!.position.x;
    if (this.facingRight && this.useRightBoundary && x > this.rightBoundaryPosition) return false;
    if (!this.facingRight && this.useLeftBoundary && x < this.leftBoundaryPosition) return false;
    return true;
  }

  private flip(force = false) {
    if (!force && (this.changeDirCounter.isRunningNotFinished() || this.regroundOnPlayerCounter.isRunningNotFinished())) return;
    this.facingRight = !this.facingRight;
    this.updateSprite();
    this.updateSensorPositions();
    this.changeDirCounter.restart();
  }

  private updateSprite() {
    const map = this.spriteMaps[this.jumpsOnPlayerJump ? 1 : 0];
    const s = map && (this.facingRight ? map.right : map.left);
    if (s && this.go.sprite) this.go.sprite.sprite = s;
  }

  private updateSensorPositions() {
    const wc = this.wallCheck!;
    if ((this.facingRight && wc.local.tx < 0) || (!this.facingRight && wc.local.tx > 0)) {
      for (const g of [wc, this.groundCheck!, this.centerGroundCheck!]) g.setLocal({ ...g.local, tx: -g.local.tx });
    }
  }

  private shouldGroundOnPlayer(p: PlayerController | null): boolean {
    if (!p || !this.regroundOnPlayerCounter.isFinished()) return false;
    if (p.isDashing() && p.dashDirection.y < 0) return false;
    if (p.powerup?.type === PowerupType.BlockDude && p.powerup.isActive) return false;
    return !p.dashFinishedThisFrame;
  }

  private enumerateContacts() {
    this.groundedOnBodySpeed = { x: 0, y: 0 };
    let n = 0;
    this.isGrounded = false;
    for (const cp of this.go.body!.getContacts()) {
      const ang = (Math.acos(clamp(cp.normal.y, -1, 1)) * 180) / Math.PI;
      if (ang > this.maxGroundAngle) continue;
      this.isGrounded = true;
      const orb = cp.rigidbody;
      const ignoredPlayer = !!orb?.go.getBehaviour(PlayerController) && this.regroundOnPlayerCounter.isRunningNotFinished();
      if (!orb || ignoredPlayer) continue;
      const v = orb.isDynamic ? orb.velocity : orb.hasKV ? orb.kvVelocity : { x: 0, y: 0 };
      this.groundedOnBodySpeed = { x: this.groundedOnBodySpeed.x + v.x, y: this.groundedOnBodySpeed.y + v.y };
      n++;
    }
    if (n > 0) {
      this.groundedOnBodySpeed = { x: this.groundedOnBodySpeed.x / n, y: this.groundedOnBodySpeed.y / n };
      this.wasDashLaunchedDiagonally = false;
    }
  }
}

// ---------- LauncherBase ----------

const enum LauncherState {
  Waiting,
  Launching,
  Returning,
}

export class LauncherBase extends GameBehaviour {
  launcher: SimBody | null = null;
  activator: ActivatorBase | null = null;
  currentState = LauncherState.Waiting;
  launcherAcceleration = 0.1;
  launcherMaxSpeed = 0.5;
  launcherMaxReturnSpeed = 0.15;
  launcherPosition = 0;
  launcherSpeed = 0;
  launcherSize = 1;
  launcherOffset: V2 = { x: 0, y: 0 };
  override noSave = ['launcher', 'activator'];

  override awake() {
    const d = this.data;
    this.currentState = num(d.currentState) as LauncherState;
    this.launcherAcceleration = num(d.launcherAcceleration, 0.1);
    this.launcherMaxSpeed = num(d.launcherMaxSpeed, 0.5);
    this.launcherPosition = num(d.launcherPosition);
    this.launcherSpeed = num(d.launcherSpeed);
  }

  override start() {
    const r = this.ref(this.data.launcher);
    this.launcher = (r?.obj as SimBody | undefined)?.pb ? (r!.obj as SimBody) : r?.go.attachedBody() ?? null;
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
    const base = this.go.body;
    const l = this.launcher;
    if (!l || !base) return;
    l.hasKV = true;
    l.managed = true;
    const sr = l.go.sprite;
    if (sr) this.launcherSize = sr.size[1];
    const lp = l.position, bp = base.position;
    this.launcherOffset = { x: lp.x - bp.x, y: lp.y - bp.y };
  }

  private updateLauncherPosition() {
    const base = this.go.body!;
    const a = ((base.rotation + 90) * Math.PI) / 180;
    const bp = base.position;
    this.launcher!.movePosition({ x: bp.x + Math.cos(a) * this.launcherPosition + this.launcherOffset.x, y: bp.y + Math.sin(a) * this.launcherPosition + this.launcherOffset.y });
  }

  override fixedUpdate() {
    // a missing activator / launcher throws in Unity
    if (!this.activator || !this.launcher || !this.go.body) return;
    switch (this.currentState) {
      case LauncherState.Waiting:
        if (this.activator.isActivated) {
          this.currentState = LauncherState.Launching;
          this.sound('launcher');
        }
        break;
      case LauncherState.Launching:
        this.launcherSpeed = clamp(this.launcherSpeed + this.launcherAcceleration, 0, this.launcherMaxSpeed);
        this.launcherPosition += this.launcherSpeed;
        if (this.launcherPosition >= this.launcherSize) {
          if (!this.activator.isActivated) this.currentState = LauncherState.Returning;
          this.launcherPosition = this.launcherSize;
          this.launcherSpeed = 0;
        }
        this.updateLauncherPosition();
        break;
      case LauncherState.Returning:
        this.launcherSpeed = clamp(this.launcherSpeed + this.launcherAcceleration, 0, this.launcherMaxReturnSpeed);
        this.launcherPosition -= this.launcherSpeed;
        if (this.launcherPosition <= 0) {
          this.currentState = LauncherState.Waiting;
          this.launcherPosition = 0;
          this.launcherSpeed = 0;
        }
        this.updateLauncherPosition();
        break;
    }
  }
}